import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getBilling } from '@/api/billing'
import { loadOrderDetailBillingExpanded, saveOrderDetailBillingExpanded } from '@/utils/order-detail-ui'
import {
  addOrderComment,
  adminAssignInspector,
  adminUpdateOrder,
  cancelOrder,
  changeOrderStatus,
  listOrderComments,
  setExternalInspectionReportUrl,
  setFinalPrice,
  setInspectionScheduledAt,
  getOrder,
  getStatusHistory,
  logPdAccess,
  publishDraftOrder,
  returnOrderToSearch,
  type OrderListResult,
} from '@/api/orders'
import { listChatMessages } from '@/api/chat'
import { getDkpDocumentSignedUrl, getLatestDkpDocument } from '@/api/dkpGenerator'
import { getPtsData } from '@/api/pts'
import { useChatRealtime } from '@/hooks/useChatRealtime'
import { InspectorPicker } from '@/components/orders/InspectorPicker'
import { DetailHeaderSkeleton, SectionSkeleton } from '@/components/ui/Skeleton'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { StatusBadge } from '@/components/orders/StatusBadge'
import { OrderKindBadge } from '@/components/orders/OrderKindBadge'
import { IndividualOrderRefund } from '@/components/orders/IndividualOrderRefund'
import { OrderInspectionTime } from '@/components/orders/OrderInspectionTime'
import { OrderInspectorEtaBlock } from '@/components/orders/OrderInspectorEtaBlock'
import { OrderInspectorTrackBlock } from '@/components/orders/OrderInspectorTrackBlock'
import { LiveTimer } from '@/components/orders/LiveTimer'
import { Button } from '@/components/ui/Button'
import { Lightbox } from '@/components/ui/Lightbox'
import { Modal } from '@/components/ui/Modal'
import { AlertDialog, ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DeliveryAddressBlock } from '@/components/orders/DeliveryAddressBlock'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { formatMoscowDateOnly, formatMoscowDateTime, formatRub } from '@/utils/format'
import { formatLicensePlate, normalizeLicensePlate } from '@/utils/licensePlate'
import { formatGroupedInt, parseGroupedIntInput } from '@/utils/money-input'
import { formatPhoneDisplay } from '@/utils/phone'
import { resolveStoredPublicUrl } from '@/utils/storageUrl'
import {
  companyName,
  companyPhone,
  profileCompanyNameByUserId,
  profileName,
  profilePhoneById,
} from '@/utils/entities'
import type { OrderStatus } from '@/types/enums'
import type { OrderComment } from '@/types/order'
import {
  canReturnToSearch,
  getForwardTargets,
  isActiveStatus,
  isTerminalStatus,
  statusLabelRu,
} from '@/utils/status-transitions'
import { mobileStageLabel } from '@/utils/order-stage-labels'
import { useAuthStore } from '@/store/authStore'
import { isCustomerManager, isCustomerSupervisor, isCustomerUser, isStaff } from '@/utils/access'
import { validateVinSoft } from '@/utils/vinValidation'
import i18n from '@/utils/i18n'

const participantNameClassName =
  'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-accent-orange)] hover:underline'

const BILLING_SERVICE_ORDER = ['trip', 'inspection', 'deal', 'delivery'] as const

function formatIdleMinutes(totalMinutes: number): string {
  const en = i18n.language === 'en'
  if (totalMinutes <= 0) return en ? '0 min' : '0 мин'
  // Сумма минут с бэка может быть float; % 60 иначе даёт «52.47340000000003 мин»
  const total = Math.round(totalMinutes)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  if (hours > 0 && minutes > 0) return en ? `${hours}h ${minutes}m` : `${hours} ч ${minutes} мин`
  if (hours > 0) return en ? `${hours}h` : `${hours} ч`
  return en ? `${minutes}m` : `${minutes} мин`
}

function formatIdleHours(hours: number): string {
  const en = i18n.language === 'en'
  if (hours <= 0) return en ? '0h' : '0 ч'
  const whole = Number.isInteger(hours)
  const val = whole ? `${hours}` : hours.toFixed(1).replace('.0', '')
  return en ? `${val}h` : `${val} ч`
}

function ParticipantNameWithPopover({
  userId,
  roleLabel,
  isAdmin,
  profile,
  companyDisplayName,
}: {
  userId: string
  roleLabel: string
  isAdmin: boolean
  /** Pre-fetched profile (Supabase mode). Falls back to mockState if not provided. */
  profile?: { full_name: string; phone: string | null } | null
  companyDisplayName?: string | null
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const fullName = profile?.full_name ?? profileName(userId)
  const phone = profile?.phone ?? profilePhoneById(userId)
  const company = companyDisplayName ?? profileCompanyNameByUserId(userId)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (isAdmin) {
    return (
      <Link
        to={`/users/${userId}`}
        className={participantNameClassName}
        aria-label={t('orderDetail.openProfileAria', { name: fullName })}
      >
        {fullName}
      </Link>
    )
  }

  return (
    <div ref={rootRef} className="relative inline-flex flex-wrap items-center gap-1">
      <button
        type="button"
        className={participantNameClassName}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? `participant-popover-${userId}` : undefined}
        aria-label={t('orderDetail.showContactsAria', { role: roleLabel, name: fullName })}
        id={`participant-trigger-${userId}`}
      >
        {fullName}
      </button>
      {open ? (
        <div
          id={`participant-popover-${userId}`}
          role="dialog"
          aria-label={t('orderDetail.participantDialogAria', { role: roleLabel, name: fullName })}
          aria-labelledby={`participant-trigger-${userId}`}
          className="absolute left-0 top-full z-[100] mt-2 min-w-[240px] max-w-[min(100vw-2rem,320px)] rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-3 shadow-lg"
        >
          <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] font-medium text-[color:var(--stryzh-color-text-primary)]">
            {fullName}
          </div>
          {phone ? (
            <div className="mt-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
              {formatPhoneDisplay(phone)}
            </div>
          ) : null}
          {company ? (
            <div className="mt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
              {company}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Адрес заказа в карточке «Данные»: кликабельная ссылка в Яндекс.Карты
 * + кнопка «Скопировать». Если `address_city` уже содержится в строке
 * адреса (саджест ДаДата нередко возвращает «город Москва, ул. Тверская…»
 * + city = «Москва»), второй раз город не показываем.
 */
function AddressBlock({ address, city }: { address: string; city?: string | null }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const cityClean = (city ?? '').trim()
  const showCity =
    cityClean.length > 0 && !address.toLowerCase().includes(cityClean.toLowerCase())
  const fullAddress = showCity ? `${address}, ${cityClean}` : address
  const mapsUrl = `https://yandex.ru/maps/?text=${encodeURIComponent(fullAddress)}`

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(fullAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard может быть недоступен (insecure context / отказ браузера) —
      // молча игнорируем, пользователь увидит, что состояние не сменилось.
    }
  }

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          title={t('orderDetail.openInYandexMaps')}
          className="break-words font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-accent-orange)] hover:underline"
        >
          {address}
        </a>
        {showCity ? (
          <div className="mt-1 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {cityClean}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => void handleCopy()}
        title={copied ? t('orderDetail.copied') : t('orderDetail.copyAddress')}
        aria-label={copied ? t('orderDetail.addressCopied') : t('orderDetail.copyAddress')}
        className="shrink-0 rounded-md p-1 text-[color:var(--stryzh-color-text-secondary)] transition-colors hover:bg-[color:var(--stryzh-color-border-subtle)] hover:text-[color:var(--stryzh-color-text-primary)]"
      >
        {copied ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M3 8.5L6.5 12L13 5"
              stroke="var(--stryzh-color-semantic-success)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <rect
              x="3.5"
              y="3.5"
              width="9"
              height="11"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M5.5 1.5H11A1.5 1.5 0 0 1 12.5 3V11.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>
    </div>
  )
}

/**
 * Три тайла со сканами паспорта продавца + селфи (anti-fraud).
 * URL'ы приходят в `order` объект мерджом из `buyer_passports` (см. getOrder в src/api/orders.ts).
 * Каждый тайл рендерится только если соответствующий URL заполнен.
 *
 * Используется в двух местах: (1) внутри ПДн-блока админа `has_pd_access`,
 * (2) отдельным блоком для дилера-заказчика и его руководителя — у них
 * RLS на `buyer_passports` разрешает SELECT по своему заказу/компании.
 */
function PassportScansTiles({
  order,
  setLightboxSrc,
}: {
  order: {
    passport_main_scan_url?: string | null
    passport_registration_scan_url?: string | null
    passport_selfie_url?: string | null
  }
  setLightboxSrc: (url: string | null) => void
}) {
  const { t } = useTranslation()
  const items: Array<{ url: string; aria: string; title?: string }> = []
  const main = order.passport_main_scan_url
    ? resolveStoredPublicUrl(order.passport_main_scan_url) ?? ''
    : ''
  const reg = order.passport_registration_scan_url
    ? resolveStoredPublicUrl(order.passport_registration_scan_url) ?? ''
    : ''
  const selfie = order.passport_selfie_url
    ? resolveStoredPublicUrl(order.passport_selfie_url) ?? ''
    : ''
  if (main) items.push({ url: main, aria: t('orderDetail.passportMainScanAria') })
  if (reg) items.push({ url: reg, aria: t('orderDetail.passportRegistrationScanAria') })
  if (selfie) {
    items.push({
      url: selfie,
      aria: t('orderDetail.passportSelfieAria'),
      title: t('orderDetail.passportSelfieTitle'),
    })
  }
  if (items.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <button
          key={it.url}
          type="button"
          className="overflow-hidden rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--stryzh-color-accent-orange)]"
          onClick={() => setLightboxSrc(it.url)}
          aria-label={it.aria}
          title={it.title}
        >
          <img
            src={it.url}
            alt=""
            className="h-28 w-auto max-w-[200px] object-cover"
          />
        </button>
      ))}
    </div>
  )
}

export function OrderDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const profile = useAuthStore((s) => s.profile)
  const [statusOpen, setStatusOpen] = useState(false)
  const [inspectorPickerOpen, setInspectorPickerOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [nextStatus, setNextStatus] = useState<OrderStatus | ''>('')
  // Поля, которые админ может заполнить прямо в модалке перехода,
  // если в моб. приложении инспектор/менеджер их не успел внести:
  // - reportUrlInput → orders.external_inspection_report_url (INSPECTION→APPRAISAL)
  // - finalPriceInput → orders.final_price (APPRAISAL→BUYING)
  const [reportUrlInput, setReportUrlInput] = useState('')
  const [finalPriceInput, setFinalPriceInput] = useState('')
  const [transitionFieldError, setTransitionFieldError] = useState<string | null>(null)
  const [pdOpen, setPdOpen] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  // Двухшаговое подтверждение перед переводом в терминальный статус
  // (COMPLETED/CANCELLED/REFUSED_*). См. §UX: «обязательное подтверждение
  // при переводе заказа в терминальный статус».
  const [terminalConfirmOpen, setTerminalConfirmOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [returnSearchOpen, setReturnSearchOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const [billingExpanded, setBillingExpanded] = useState(false)

  // Редактирование заказа (admin/dispatcher)
  const [editOpen, setEditOpen] = useState(false)
  const [editDraft, setEditDraft] = useState<{
    brand: string
    model: string
    address: string
    address_city: string
    seller_full_name: string
    seller_phone: string
    vin: string
    /** Год выпуска — строка для удобства <input>, парсим в int при save. */
    vehicle_year: string
    /** Гос. номер — хранится с пробелами для отображения, нормализуем при save. */
    license_plate: string
  } | null>(null)
  const [editError, setEditError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const stored = loadOrderDetailBillingExpanded(id)
    setBillingExpanded(stored === true)
  }, [id])

  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(id),
    enabled: !!id,
    /**
     * Если пришли из `/orders`, соответствующая строка уже есть в кэше.
     * Берём её как `initialData` — карточка рисуется без «Загрузка…»,
     * а свежий `getOrder` (со всеми ПДн/компаниями) прилетит фоном.
     */
    initialData: () => {
      if (!id) return undefined
      // Внимание: getQueriesData({ queryKey: ['orders'] }) матчит ВСЕ ключи
      // с префиксом ['orders', ...] — включая ['orders', 'active-count', ...]
      // (там лежит просто number) и любые будущие подключи. Без `Array.isArray`
      // на v?.items получаем `undefined.find(...)` и `Unexpected Application
      // Error: undefined is not an object (evaluating 'v?.items.find')` при
      // первом монтировании после createOrder → navigate(`/orders/${id}`).
      const lists = qc.getQueriesData<OrderListResult>({ queryKey: ['orders'] })
      for (const [, v] of lists) {
        if (!v || !Array.isArray(v.items)) continue
        const hit = v.items.find((o) => o.id === id)
        if (hit) return hit
      }
      return undefined
    },
    initialDataUpdatedAt: () =>
      qc.getQueryState(['orders', 'list'])?.dataUpdatedAt,
  })

  const { data: history = [] } = useQuery({
    queryKey: ['order-history', id],
    queryFn: () => getStatusHistory(id),
    enabled: !!id,
  })

  const { data: chat = [] } = useQuery({
    queryKey: ['order-chat', id],
    queryFn: () => listChatMessages(id),
    enabled: !!id,
  })
  // Realtime — новые сообщения в чате заказа без F5.
  useChatRealtime(id)

  const {
    data: billing,
    isLoading: billingLoading,
    isError: billingError,
    error: billingErr,
  } = useQuery({
    queryKey: ['billing', id],
    queryFn: () => getBilling(id),
    enabled: !!id,
    staleTime: 15_000,
  })

  // Текущая (`is_current=true`) запись из `order_documents` для отображения
  // в блоке «ДКП» — № договора, дата, ссылка на сгенерированный PDF.
  // Ключ совпадает с тем, что использует /dkp-generator при invalidate после
  // ручной перегенерации → блок обновится без перезагрузки страницы.
  const { data: latestDkp } = useQuery({
    queryKey: ['latest-dkp', id],
    queryFn: () => getLatestDkpDocument(id),
    enabled: !!id,
  })

  // Данные ПТС (`pts_data`) — для одноимённого блока на карточке заказа.
  // Заполняется мобилкой через `upsertPtsData`; админка только читает.
  const { data: ptsData } = useQuery({
    queryKey: ['pts-data', id],
    queryFn: () => getPtsData(id),
    enabled: !!id,
  })

  const mutStatus = useMutation({
    mutationFn: async () => {
      if (!nextStatus || !order) return
      // INSPECTION→APPRAISAL: если URL отчёта осмотра ещё не введён —
      // админ может ввести его прямо в модалке. Сохраняем до change_order_status.
      if (
        order.status === 'INSPECTION' &&
        nextStatus === 'APPRAISAL' &&
        !order.external_inspection_report_url
      ) {
        const url = reportUrlInput.trim()
        if (url.length === 0) {
          throw new Error(t('orderDetail.errorRequireInspectionUrl'))
        }
        await setExternalInspectionReportUrl(id, url)
      }
      // C2C →COMPLETED (из ASSIGNED/…/INSPECTION): COMPLETED для физлица = «отчёт
      // передан», поэтому external_inspection_report_url обязателен. Если уже
      // выставлен — не требуем повторного ввода. Postgres-триггер
      // orders_c2c_complete_requires_report_bu — второй эшелон.
      if (
        order.customer_kind === 'individual' &&
        nextStatus === 'COMPLETED' &&
        !order.external_inspection_report_url
      ) {
        const url = reportUrlInput.trim()
        if (url.length === 0) {
          throw new Error(t('orderDetail.errorRequireReportLink'))
        }
        await setExternalInspectionReportUrl(id, url)
      }
      // APPRAISAL→BUYING: аналогично для выкупной цены.
      if (
        order.status === 'APPRAISAL' &&
        nextStatus === 'BUYING' &&
        (!order.final_price || order.final_price <= 0)
      ) {
        const raw = finalPriceInput.replace(/[^\d]/g, '')
        const price = Number(raw)
        if (!Number.isFinite(price) || price <= 0) {
          throw new Error(t('orderDetail.errorRequireBuyoutPrice'))
        }
        await setFinalPrice(id, price)
      }
      await changeOrderStatus(id, nextStatus)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order', id] })
      void qc.invalidateQueries({ queryKey: ['billing', id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['feed-events'] })
      void qc.invalidateQueries({ queryKey: ['order-history', id] })
      setStatusOpen(false)
      setTerminalConfirmOpen(false)
      setNextStatus('')
      setReportUrlInput('')
      setFinalPriceInput('')
      setTransitionFieldError(null)
    },
    onError: (e: Error) => {
      setTerminalConfirmOpen(false)
      setTransitionFieldError(e.message)
    },
  })

  const mutAssignInspector = useMutation({
    // Сначала инспектор, потом время: событие Сберу «Назначен осмотр» уходит по
    // появлению времени и должно уже знать инспектора.
    mutationFn: async ({ inspectorId, inspectionAt }: { inspectorId: string; inspectionAt: string | null }) => {
      await adminAssignInspector(id, inspectorId)
      if (inspectionAt && inspectionAt !== order?.inspection_scheduled_at) {
        await setInspectionScheduledAt(id, inspectionAt)
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order', id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['order-history', id] })
      setInspectorPickerOpen(false)
      setStatusOpen(false)
      setNextStatus('')
    },
    onError: (e: Error) => {
      setInspectorPickerOpen(false)
      setStatusOpen(false)
      setNextStatus('')
      alert(e.message)
    },
  })

  const mutReturnSearch = useMutation({
    mutationFn: async () => {
      await returnOrderToSearch(id)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order', id] })
      void qc.invalidateQueries({ queryKey: ['billing', id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['feed-events'] })
      void qc.invalidateQueries({ queryKey: ['order-history', id] })
      setReturnSearchOpen(false)
    },
    onError: (e: Error) => {
      setReturnSearchOpen(false)
      alert(e.message)
    },
  })

  const mutCancel = useMutation({
    mutationFn: async () => {
      await cancelOrder(id, reason)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order', id] })
      void qc.invalidateQueries({ queryKey: ['billing', id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['feed-events'] })
      setCancelOpen(false)
      setCancelConfirmOpen(false)
      setReason('')
    },
    onError: (e: Error) => {
      setCancelConfirmOpen(false)
      setCancelError(e.message)
    },
  })

  const mutAdminEdit = useMutation({
    mutationFn: async () => {
      if (!editDraft || !order) return
      const parsedYear = editDraft.vehicle_year.trim()
        ? Number.parseInt(editDraft.vehicle_year, 10)
        : null
      const normalizedPlate = normalizeLicensePlate(editDraft.license_plate)
      await adminUpdateOrder(id, {
        brand: editDraft.brand !== order.brand ? editDraft.brand : null,
        model: editDraft.model !== order.model ? editDraft.model : null,
        address: editDraft.address !== order.address ? editDraft.address : null,
        address_city:
          editDraft.address_city !== (order.address_city ?? '')
            ? editDraft.address_city
            : null,
        seller_name:
          editDraft.seller_full_name !== (order.seller_full_name ?? '')
            ? editDraft.seller_full_name
            : null,
        seller_phone:
          editDraft.seller_phone !== (order.seller_phone ?? '')
            ? editDraft.seller_phone
            : null,
        vin: editDraft.vin !== (order.vin ?? '') ? editDraft.vin || null : null,
        vehicle_year:
          parsedYear !== (order.vehicle_year ?? null) ? parsedYear : null,
        license_plate:
          normalizedPlate !== (order.license_plate ?? null)
            ? normalizedPlate
            : null,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order', id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      setEditOpen(false)
      setEditDraft(null)
      setEditError(null)
    },
    onError: (e: Error) => {
      setEditError(e.message)
    },
  })

  if (orderLoading && !order) {
    return (
      <div className="w-full max-w-none px-6 py-4 lg:px-8">
        <DetailHeaderSkeleton />
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-5">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
            <SectionSkeleton lines={4} />
            <SectionSkeleton lines={4} />
          </div>
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <SectionSkeleton lines={3} />
            <SectionSkeleton lines={3} />
          </div>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="p-8 text-[var(--stryzh-color-text-secondary)]">
        {t('orderDetail.notFound')}{' '}
        <button type="button" className="text-[var(--stryzh-color-accent-orange)]" onClick={() => nav(-1)}>
          {t('orderDetail.back')}
        </button>
      </div>
    )
  }

  // Проверка доступа по роли: менеджер видит только свои заказы, руководитель — заказы своей компании
  if (isCustomerManager(profile) && order.manager_id !== profile?.id) {
    return (
      <div className="p-8 text-[var(--stryzh-color-text-secondary)]">
        {t('orderDetail.noAccess')}{' '}
        <button type="button" className="text-[var(--stryzh-color-accent-orange)]" onClick={() => nav('/orders')}>
          {t('orderDetail.toOrders')}
        </button>
      </div>
    )
  }
  if (isCustomerSupervisor(profile) && order.customer_company_id !== profile?.company_id) {
    return (
      <div className="p-8 text-[var(--stryzh-color-text-secondary)]">
        {t('orderDetail.noAccess')}{' '}
        <button type="button" className="text-[var(--stryzh-color-accent-orange)]" onClick={() => nav('/orders')}>
          {t('orderDetail.toOrders')}
        </button>
      </div>
    )
  }

  const shortOrderId = order.id.slice(0, 8).toUpperCase()
  const title =
    `№ ${shortOrderId} · ${order.brand} ${order.model}` +
    (order.vin ? ` · VIN ${order.vin}` : '')
  const targets = getForwardTargets(order.status as OrderStatus, order.customer_kind)
  const canAct = isActiveStatus(order.status as OrderStatus)
  const lateStatuses = [
    'ARRIVED',
    'INSPECTION',
    'APPRAISAL',
    'BUYING',
    'DOCUMENT_PREPARATION',
    'PAYMENT_TRANSFER',
    'TRANSPORTATION',
  ]
  const strongCancel = lateStatuses.includes(order.status)

  const synthetic = [
    order.dkp_formed_at
      ? { t: order.dkp_formed_at, label: t('orderDetail.syntheticDkpFormed') }
      : null,
    order.payment_sent_at
      ? { t: order.payment_sent_at, label: t('orderDetail.syntheticMoneySent') }
      : null,
    order.transportation_started_at
      ? { t: order.transportation_started_at, label: t('orderDetail.syntheticTransportStarted') }
      : null,
    order.vehicle_handed_to_customer_at
      ? { t: order.vehicle_handed_to_customer_at, label: t('orderDetail.syntheticTransportFinished') }
      : null,
    order.transportation_confirmed_at
      ? {
          t: order.transportation_confirmed_at,
          label: t('orderDetail.syntheticTransportConfirmed'),
        }
      : null,
  ].filter(Boolean) as { t: string; label: string }[]

  const showTransportBlock =
    order.status === 'TRANSPORTATION' || order.status === 'COMPLETED'

  const BUYOUT_VISIBLE_STATUSES = new Set<OrderStatus>([
    'BUYING',
    'DOCUMENT_PREPARATION',
    'PAYMENT_TRANSFER',
    'TRANSPORTATION',
    'COMPLETED',
    'REFUSED_BUYOUT',
  ])
  const showBuyoutBlock = BUYOUT_VISIBLE_STATUSES.has(order.status as OrderStatus)

  // Геопозиция инспектора имеет смысл только пока он реально едет/работает.
  // Список синхронизирован с view inspector_position_for_dealer (фаза гео-1):
  // на DRAFT/SEARCH/ASSIGNED view возвращает 0 строк, на терминальных — тоже.
  const INSPECTOR_LIVE_STATUSES = new Set<OrderStatus>([
    'DRIVING',
    'ARRIVED',
    'INSPECTION',
    'APPRAISAL',
    'BUYING',
    'DOCUMENT_PREPARATION',
    'PAYMENT_TRANSFER',
    'TRANSPORTATION',
  ])

  const hasDkpContent =
    Boolean(order.dkp_template_url) ||
    (order.signed_dkp_page_urls?.length ?? 0) > 0 ||
    Boolean(order.dkp_formed_at) ||
    Boolean(latestDkp)

  const showInspectionBlock = Boolean(order.external_inspection_report_url)

  const customerPhone =
    order.customer_kind === 'individual'
      ? order.individual_customer_phone
      : (order.customer_company?.phone ?? companyPhone(order.customer_company_id))
  const dataLeftCol =
    'min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4'
  const dataRightCol = 'min-w-0 min-[640px]:pl-4'
  const dataValue =
    'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]'
  const isAdmin = profile?.role === 'admin'
  const isCustomer = isCustomerUser(profile)
  const canCancel = isStaff(profile) && canAct
  const billingItemsByService = new Map(billing?.items.map((item) => [item.service, item]) ?? [])
  const isOrderTerminal = isTerminalStatus(order.status)
  const displayedBillingRows = BILLING_SERVICE_ORDER.map((service) => ({
    service,
    label: t(`billingService.${service}`),
    item: billingItemsByService.get(service),
  })).filter((row) => (isOrderTerminal ? row.item?.is_confirmed === true : true))
  const serviceTotalCustomer = displayedBillingRows.reduce(
    (acc, row) => acc + (row.item?.is_confirmed ? (row.item.amount_customer ?? 0) : 0),
    0,
  )
  const serviceTotalInspector = displayedBillingRows.reduce(
    (acc, row) => acc + (row.item?.is_confirmed ? (row.item.amount_inspector ?? 0) : 0),
    0,
  )
  const serviceTotalMargin = serviceTotalCustomer - serviceTotalInspector
  const idleRawMinutes = billing?.idle.periods.reduce((acc, period) => acc + period.minutes, 0) ?? 0
  const idleRateCustomer = billing?.idle.total_hours
    ? billing.idle.amount_customer / billing.idle.total_hours
    : 0
  const idleRateInspector = billing?.idle.total_hours
    ? billing.idle.amount_inspector / billing.idle.total_hours
    : 0

  const card =
    'rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4'

  // Статусы, после которых VIN нельзя менять
  const VIN_LOCKED_STATUSES = new Set([
    'APPRAISAL',
    'BUYING',
    'DOCUMENT_PREPARATION',
    'PAYMENT_TRANSFER',
    'TRANSPORTATION',
    'COMPLETED',
    'CANCELLED',
    'REFUSED_BY_INSPECTOR',
    'REFUSED_BY_SELLER',
    'REFUSED_BUYOUT',
  ])
  const TERMINAL_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'])
  const canEditFields = isStaff(profile) && !TERMINAL_STATUSES.has(order.status)
  const vinLocked = VIN_LOCKED_STATUSES.has(order.status)

  function startEdit() {
    if (!order) return
    setEditDraft({
      brand: order.brand,
      model: order.model,
      address: order.address,
      address_city: order.address_city ?? '',
      seller_full_name: order.seller_full_name ?? '',
      seller_phone: order.seller_phone ?? '',
      vin: order.vin ?? '',
      vehicle_year: order.vehicle_year != null ? String(order.vehicle_year) : '',
      license_plate: order.license_plate ? formatLicensePlate(order.license_plate) : '',
    })
    setEditOpen(true)
    setEditError(null)
  }


  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <AlertDialog
        open={cancelError != null}
        title={t('orderDetail.cancelDialogTitle')}
        message={cancelError ?? ''}
        onClose={() => setCancelError(null)}
      />
      <Breadcrumbs
        items={[
          { label: t('orderDetail.breadcrumbOrders'), to: '/orders' },
          { label: title },
        ]}
      />
      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="min-w-0 break-words font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-hero)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
              {title}
            </h1>
            <StatusBadge status={order.status as OrderStatus} />
            <OrderKindBadge kind={order.customer_kind} />
            {order.inspection_scheduled_at ? (
              <span className="rounded-full border border-[color:var(--stryzh-color-border-default)] px-2.5 py-0.5 text-xs text-[color:var(--stryzh-color-text-primary)]">
                {t('orderDetail.inspectionAtBadge', { at: formatMoscowDateTime(order.inspection_scheduled_at) })}
              </span>
            ) : null}
            <LiveTimer
              size="display"
              status={order.status as OrderStatus}
              createdAt={order.created_at}
              statusUpdatedAt={order.status_updated_at}
            />
          </div>
          <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
            {order.status === 'DRAFT' && isStaff(profile) ? (
              <Button
                type="button"
                variant="primary"
                disabled={publishing}
                onClick={() => {
                  setPublishing(true)
                  publishDraftOrder(id)
                    .then(() => qc.invalidateQueries({ queryKey: ['order', id] }))
                    .catch((e: unknown) => alert(e instanceof Error ? e.message : t('orderDetail.error')))
                    .finally(() => setPublishing(false))
                }}
              >
                {publishing ? t('orderDetail.publishing') : t('orderDetail.publish')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="primary"
              disabled={!canAct || targets.length === 0}
              onClick={() => setStatusOpen(true)}
            >
              {t('orderDetail.changeStatus')}
            </Button>
            {order.status === 'SEARCH' && isStaff(profile) ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => nav(`/orders/${order.id}/nearest-inspectors`)}
              >
                {t('orderDetail.findNearestInspectors')}
              </Button>
            ) : null}
            {isStaff(profile) && canReturnToSearch(order.status as OrderStatus) ? (
              <Button
                type="button"
                variant="outline"
                disabled={mutReturnSearch.isPending}
                onClick={() => setReturnSearchOpen(true)}
              >
                {t('orderDetail.returnToSearch')}
              </Button>
            ) : null}
            {!isCustomer ? (
              <Button
                type="button"
                variant="danger"
                disabled={!canCancel}
                onClick={() => setCancelOpen(true)}
              >
                {t('orderDetail.cancelOrder')}
              </Button>
            ) : null}
          </div>
        </div>
        {(order.vehicle_year || order.license_plate) && (
          <p className="font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {[
              order.vehicle_year ? `${order.vehicle_year}` : null,
              order.license_plate ? formatLicensePlate(order.license_plate) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('orderDetail.created', { date: formatMoscowDateTime(order.created_at) })}
        </p>
      </header>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-5 lg:items-start lg:gap-6">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('orderDetail.participants')}</h2>
            <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
              <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                <div className="stryzh-input-label">{order.customer_kind === 'individual' ? t('orderDetail.buyer') : t('orderDetail.customer')}</div>
                <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                  {order.customer_kind === 'individual' ? (
                    <>
                      <span>{order.individual_customer_name ?? '—'}</span>
                      {order.lead_id ? (
                        <Link
                          to={`/inspection-leads/${order.lead_id}`}
                          className="mt-1 block text-xs text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        >
                          {t('orderDetail.leadSource')}
                        </Link>
                      ) : null}
                    </>
                  ) : isCustomer ? (
                    <span>{order.customer_company?.name ?? companyName(order.customer_company_id)}</span>
                  ) : (
                    <Link
                      className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                      to={`/companies/${order.customer_company_id}`}
                    >
                      {order.customer_company?.name ?? companyName(order.customer_company_id)}
                    </Link>
                  )}
                </div>
              </div>
              <div className="min-w-0 min-[640px]:pl-4">
                <div className="stryzh-input-label">{t('orderDetail.contractor')}</div>
                <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                  {(() => {
                    // Источник: явно записанный contractor_company_id
                    // или (фолбэк) компания инспектора, если поле в заказе
                    // не заполнено (см. fallback в getOrder).
                    const cid = order.contractor_company_id ?? order.contractor_company?.id ?? null
                    const cname = order.contractor_company?.name ?? (order.contractor_company_id ? companyName(order.contractor_company_id) : null)
                    if (!cid || !cname) return '—'
                    return isCustomer ? (
                      <span>{cname}</span>
                    ) : (
                      <Link
                        className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        to={`/companies/${cid}`}
                      >
                        {cname}
                      </Link>
                    )
                  })()}
                </div>
              </div>
              <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                <div className="stryzh-input-label">{order.customer_kind === 'individual' ? t('orderDetail.phone') : t('orderDetail.manager')}</div>
                <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                  {order.customer_kind === 'individual' ? (
                    <span>{order.individual_customer_phone ? formatPhoneDisplay(order.individual_customer_phone) : '—'}</span>
                  ) : (
                    <ParticipantNameWithPopover
                      userId={order.manager_id!}
                      roleLabel={t('orderDetail.manager')}
                      isAdmin={isAdmin}
                      profile={order.manager}
                      companyDisplayName={order.customer_company?.name}
                    />
                  )}
                </div>
              </div>
              <div className="min-w-0 min-[640px]:pl-4">
                <div className="stryzh-input-label">{t('orderDetail.inspector')}</div>
                <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                  {order.inspector_id ? (
                    <ParticipantNameWithPopover
                      userId={order.inspector_id}
                      roleLabel={t('orderDetail.inspector')}
                      isAdmin={isAdmin}
                      profile={order.inspector_profile}
                      companyDisplayName={order.contractor_company?.name}
                    />
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            </div>
          </section>

          <IndividualOrderRefund order={order} />

          {order.inspector_id && INSPECTOR_LIVE_STATUSES.has(order.status) ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.inspectorLocation')}</h2>
              <OrderInspectorEtaBlock
                orderId={order.id}
                inspectorId={order.inspector_id}
                orderLat={order.lat ?? null}
                orderLon={order.lon ?? null}
              />
            </section>
          ) : null}

          {order.inspector_id ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.inspectorTrack')}</h2>
              <OrderInspectorTrackBlock
                orderId={order.id}
                inspectorId={order.inspector_id}
                orderCreatedAt={order.created_at}
                isOrderActive={!isTerminalStatus(order.status)}
                orderLat={order.lat ?? null}
                orderLon={order.lon ?? null}
              />
            </section>
          ) : null}

          <section className={card}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="stryzh-heading-card mb-0">{t('orderDetail.details')}</h2>
              {isStaff(profile) && !editOpen ? (
                <button
                  type="button"
                  onClick={startEdit}
                  className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)] transition-colors hover:text-[color:var(--stryzh-color-accent-orange)]"
                >
                  {t('orderDetail.edit')}
                </button>
              ) : null}
            </div>

            {editOpen && editDraft ? (
              <div className="space-y-3">
                {editError ? (
                  <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-semantic-error)]">
                    {editError}
                  </p>
                ) : null}
                <div className="grid gap-3 min-[640px]:grid-cols-2">
                  <Input
                    label={t('orderDetail.make')}
                    value={editDraft.brand}
                    onChange={(e) => setEditDraft({ ...editDraft, brand: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <Input
                    label={t('orderDetail.model')}
                    value={editDraft.model}
                    onChange={(e) => setEditDraft({ ...editDraft, model: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <Input
                    label={t('orderDetail.address')}
                    value={editDraft.address}
                    onChange={(e) => setEditDraft({ ...editDraft, address: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <Input
                    label={t('orderDetail.city')}
                    value={editDraft.address_city}
                    onChange={(e) => setEditDraft({ ...editDraft, address_city: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <Input
                    label={t('orderDetail.sellerName')}
                    value={editDraft.seller_full_name}
                    onChange={(e) => setEditDraft({ ...editDraft, seller_full_name: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <Input
                    label={t('orderDetail.sellerPhone')}
                    value={editDraft.seller_phone}
                    onChange={(e) => setEditDraft({ ...editDraft, seller_phone: e.target.value })}
                    disabled={!canEditFields}
                  />
                  <div className="min-[640px]:col-span-2">
                    <Input
                      label={vinLocked ? t('orderDetail.vinLocked') : 'VIN'}
                      value={editDraft.vin}
                      onChange={(e) =>
                        setEditDraft({
                          ...editDraft,
                          vin: e.target.value.toUpperCase().slice(0, 17),
                        })
                      }
                      disabled={vinLocked || !canEditFields}
                      maxLength={17}
                    />
                    {(() => {
                      const hint = validateVinSoft(editDraft.vin)
                      return hint ? (
                        <div className="mt-1 text-xs text-[color:var(--stryzh-color-warning,#e0a800)]">
                          {hint}
                        </div>
                      ) : null
                    })()}
                  </div>
                  <Input
                    label={t('orderDetail.year')}
                    value={editDraft.vehicle_year}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        vehicle_year: e.target.value.replace(/\D/g, '').slice(0, 4),
                      })
                    }
                    disabled={!canEditFields}
                    placeholder="2018"
                    inputMode="numeric"
                    maxLength={4}
                  />
                  <Input
                    label={t('orderDetail.plate')}
                    value={editDraft.license_plate}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        license_plate: formatLicensePlate(e.target.value),
                      })
                    }
                    disabled={!canEditFields}
                    placeholder="А 123 ВС 77"
                  />
                </div>
                {TERMINAL_STATUSES.has(order.status) ? (
                  <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)]">
                    {t('orderDetail.terminalNoEdit')}
                  </p>
                ) : null}
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="primary"
                    disabled={mutAdminEdit.isPending || !canEditFields}
                    onClick={() => mutAdminEdit.mutate()}
                  >
                    {mutAdminEdit.isPending ? t('orderDetail.saving') : t('orderDetail.save')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditOpen(false)
                      setEditDraft(null)
                      setEditError(null)
                    }}
                  >
                    {t('orderDetail.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <dl className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                <div className={dataLeftCol}>
                  <dt className="stryzh-input-label">{t('orderDetail.address')}</dt>
                  <dd className={dataValue}>
                    <AddressBlock address={order.address} city={order.address_city} />
                  </dd>
                </div>
                <div className={dataRightCol}>
                  <OrderInspectionTime
                    orderId={order.id}
                    value={order.inspection_scheduled_at}
                    canEdit={canEditFields}
                  />
                </div>
                <div className={dataLeftCol}>
                  <dt className="stryzh-input-label">{t('orderDetail.phone')}</dt>
                  <dd className={dataValue}>{formatPhoneDisplay(customerPhone)}</dd>
                </div>
                <div className={dataRightCol}>
                  <dt className="stryzh-input-label">{t('orderDetail.plate')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.license_plate ? formatLicensePlate(order.license_plate) : '—'}
                  </dd>
                </div>
                <div className={dataLeftCol}>
                  <dt className="stryzh-input-label">VIN</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.vin ?? '—'}
                  </dd>
                </div>
                {order.order_comment ? (
                  <div className="min-w-0 min-[640px]:col-span-2">
                    <dt className="stryzh-input-label">{t('orderDetail.comment')}</dt>
                    <dd className={`whitespace-pre-wrap break-words ${dataValue}`}>{order.order_comment}</dd>
                  </div>
                ) : null}
                {order.client_comment ? (
                  <div className="min-w-0 min-[640px]:col-span-2">
                    <dt className="stryzh-input-label">{t('orderDetail.clientComment')}</dt>
                    <dd className={`whitespace-pre-wrap break-words ${dataValue}`}>{order.client_comment}</dd>
                  </div>
                ) : null}
              </dl>
            )}
          </section>

          <section className={card}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="stryzh-heading-card mb-0">{t('orderDetail.balance')}</h2>
              {billing ? (
                <button
                  type="button"
                  className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)] transition-colors hover:text-[color:var(--stryzh-color-accent-orange)]"
                  onClick={() =>
                    setBillingExpanded((prev) => {
                      const next = !prev
                      saveOrderDetailBillingExpanded(id, next)
                      return next
                    })
                  }
                  aria-expanded={billingExpanded}
                  aria-controls="order-billing-details"
                >
                  {billingExpanded ? t('orderDetail.collapse') : t('orderDetail.expand')}
                </button>
              ) : null}
            </div>
            {billingError ? (
              <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-semantic-error)]">
                {billingErr instanceof Error
                  ? billingErr.message
                  : t('orderDetail.billingLoadError')}
              </p>
            ) : billingLoading && !billing ? (
              <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
                {t('orderDetail.calculating')}
              </p>
            ) : billing ? (
              <dl className="grid gap-4">
                {isStaff(profile) ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <dt className="stryzh-input-label">{t('orderDetail.customerTotal')}</dt>
                      <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xl)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
                        {formatRub(billing.totals.total_customer)}
                      </dd>
                    </div>
                    <div>
                      <dt className="stryzh-input-label">{t('orderDetail.contractorPayout')}</dt>
                      <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xl)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
                        {formatRub(billing.totals.total_inspector)}
                      </dd>
                    </div>
                    <div>
                      <dt className="stryzh-input-label">{t('orderDetail.platformFee')}</dt>
                      <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xl)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
                        {formatRub(billing.totals.margin)}
                      </dd>
                    </div>
                  </div>
                ) : (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.customerTotalDue')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xl)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
                      {formatRub(billing.totals.total_customer)}
                    </dd>
                  </div>
                )}
                <div
                  id="order-billing-details"
                  className={`grid transition-all duration-300 ease-in-out ${
                    billingExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                  aria-hidden={!billingExpanded}
                >
                  <div className="overflow-hidden">
                    <div className="mb-4 rounded-lg border border-[color:var(--stryzh-color-border-default)]">
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(96px,auto)_minmax(96px,auto)_minmax(96px,auto)] gap-2 border-b border-[color:var(--stryzh-color-border-default)] px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] uppercase tracking-[0.04em] text-[color:var(--stryzh-color-text-secondary)]">
                        <span>{t('orderDetail.service')}</span>
                        <span className="text-right">{t('orderDetail.colCustomer')}</span>
                        <span className="text-right">{t('orderDetail.colContractor')}</span>
                        <span className="text-right">{t('orderDetail.colFee')}</span>
                      </div>
                      <div className="divide-y divide-[color:var(--stryzh-color-border-subtle)]">
                        {displayedBillingRows.map((row) => {
                          const isConfirmed = row.item?.is_confirmed ?? false
                          const marker = row.item ? (isConfirmed ? '✓' : '○') : null
                          const markerClass = isConfirmed
                            ? 'text-[color:var(--stryzh-color-accent-orange)]'
                            : 'text-[color:var(--stryzh-color-text-secondary)]'
                          const textClass =
                            row.item && isConfirmed
                              ? 'text-[color:var(--stryzh-color-text-primary)]'
                              : 'text-[color:rgba(255,255,255,0.45)]'
                          return (
                            <div
                              key={row.service}
                              className="grid grid-cols-[minmax(0,1fr)_minmax(96px,auto)_minmax(96px,auto)_minmax(96px,auto)] gap-2 px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)]"
                            >
                              <span className={`flex items-center gap-2 ${textClass}`}>
                                <span className={`inline-block w-3 text-center text-xs ${markerClass}`}>
                                  {marker ?? ''}
                                </span>
                                {row.label}
                              </span>
                              <span className={`text-right ${textClass}`}>
                                {row.item ? formatRub(row.item.amount_customer) : '—'}
                              </span>
                              <span className={`text-right ${textClass}`}>
                                {row.item ? formatRub(row.item.amount_inspector) : '—'}
                              </span>
                              <span className={`text-right ${textClass}`}>
                                {row.item ? formatRub(row.item.amount_customer - row.item.amount_inspector) : '—'}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(96px,auto)_minmax(96px,auto)_minmax(96px,auto)] gap-2 border-t border-[color:var(--stryzh-color-border-default)] px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-text-primary)]">
                        <span>{isOrderTerminal ? t('orderDetail.totalSum') : t('orderDetail.estimatedSum')}</span>
                        <span className="text-right">{formatRub(serviceTotalCustomer)}</span>
                        <span className="text-right">{formatRub(serviceTotalInspector)}</span>
                        <span className="text-right">{formatRub(serviceTotalMargin)}</span>
                      </div>
                    </div>

                    {billing.idle.total_hours > 0 ? (
                      <div className="mb-3 rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[rgba(255,255,255,0.02)] px-3 py-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <h3 className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-text-primary)]">
                            {t('orderDetail.idle')}
                          </h3>
                          <span className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                            {formatIdleMinutes(idleRawMinutes)} → {formatIdleHours(billing.idle.total_hours)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {billing.idle.periods.map((period, idx) => (
                            <div
                              key={`${period.status}-${idx}`}
                              className="grid grid-cols-[minmax(0,1fr)_minmax(120px,auto)_minmax(120px,auto)] gap-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]"
                            >
                              <span>
                                {period.status}: {formatIdleHours(period.rounded_hours)}
                              </span>
                              <span className="text-right">
                                {formatRub(Math.round(idleRateCustomer * period.rounded_hours))}
                              </span>
                              <span className="text-right">
                                {formatRub(Math.round(idleRateInspector * period.rounded_hours))}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 grid grid-cols-[minmax(0,1fr)_minmax(120px,auto)_minmax(120px,auto)] gap-2 border-t border-[color:var(--stryzh-color-border-subtle)] pt-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-text-primary)]">
                          <span>{t('orderDetail.idleTotal')}</span>
                          <span className="text-right">{formatRub(billing.idle.amount_customer)}</span>
                          <span className="text-right">{formatRub(billing.idle.amount_inspector)}</span>
                        </div>
                      </div>
                    ) : null}

                    <p className="mb-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                      {t('orderDetail.billingLegend')}
                    </p>
                  </div>
                </div>
              </dl>
            ) : null}
          </section>

          {showInspectionBlock ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.inspectionSection')}</h2>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] font-medium text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                  href={order.external_inspection_report_url!}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('orderDetail.openInCarinspect')}
                </a>
                {order.external_inspection_report_verified_at ? (
                  <span className="inline-flex items-center gap-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-semantic-success)]">
                    <span aria-hidden>✓</span> {t('orderDetail.verified')}
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}

          {showBuyoutBlock ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.buyout')}</h2>
              <dl className="grid gap-4">
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.appraisalPrice')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {formatRub(order.desired_price)}
                  </dd>
                </div>
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.finalPrice')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {formatRub(order.final_price)}
                  </dd>
                </div>
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.payment')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.payment_sent_at
                      ? formatMoscowDateTime(order.payment_sent_at)
                      : '—'}
                  </dd>
                </div>
                {profile?.has_pd_access ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.personalData')}</dt>
                    <dd>
                      {!pdOpen ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={async () => {
                            await logPdAccess(id)
                            void qc.invalidateQueries({ queryKey: ['pd-log'] })
                            setPdOpen(true)
                          }}
                        >
                          {t('orderDetail.showPersonalData')}
                        </Button>
                      ) : (
                        <div className="grid gap-4">
                          {(() => {
                            const passportFullName = [
                              order.passport_last_name,
                              order.passport_first_name,
                              order.passport_middle_name,
                            ]
                              .filter(Boolean)
                              .join(' ')
                              .trim()
                            const fullName = passportFullName || order.seller_full_name || '—'
                            const seriesNumber =
                              order.passport_series_number ?? order.passport_masked ?? '—'
                            const registrationAddress = [
                              order.passport_registration_locality,
                              order.passport_registration_address,
                            ]
                              .filter(Boolean)
                              .join(', ')
                            return (
                              <>
                                <div>
                                  <div className="stryzh-input-label">{t('orderDetail.sellerName')}</div>
                                  <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                    {fullName}
                                  </div>
                                </div>
                                <div>
                                  <div className="stryzh-input-label">{t('orderDetail.passportSeriesNumber')}</div>
                                  <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                    {seriesNumber}
                                  </div>
                                </div>
                                {order.passport_birth_date ? (
                                  <div>
                                    <div className="stryzh-input-label">{t('orderDetail.passportBirthDate')}</div>
                                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                      {formatMoscowDateOnly(order.passport_birth_date)}
                                    </div>
                                  </div>
                                ) : null}
                                {order.passport_issued_by ? (
                                  <div>
                                    <div className="stryzh-input-label">{t('orderDetail.passportIssuedBy')}</div>
                                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                      {order.passport_issued_by}
                                    </div>
                                  </div>
                                ) : null}
                                {order.passport_issue_date ? (
                                  <div>
                                    <div className="stryzh-input-label">{t('orderDetail.passportIssueDate')}</div>
                                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                      {formatMoscowDateOnly(order.passport_issue_date)}
                                    </div>
                                  </div>
                                ) : null}
                                {order.passport_division_code ? (
                                  <div>
                                    <div className="stryzh-input-label">{t('orderDetail.passportDivisionCode')}</div>
                                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                      {order.passport_division_code}
                                    </div>
                                  </div>
                                ) : null}
                                {registrationAddress ? (
                                  <div>
                                    <div className="stryzh-input-label">{t('orderDetail.passportRegistrationAddress')}</div>
                                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                      {registrationAddress}
                                    </div>
                                  </div>
                                ) : null}
                                <div>
                                  <div className="stryzh-input-label">{t('orderDetail.sbpPhone')}</div>
                                  <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                                    {formatPhoneDisplay(order.payout_sbp_phone)}
                                  </div>
                                </div>
                              </>
                            )
                          })()}
                          {order.passport_main_scan_url ||
                          order.passport_registration_scan_url ||
                          order.passport_selfie_url ? (
                            <div>
                              <div className="stryzh-input-label">{t('orderDetail.passportScans')}</div>
                              <PassportScansTiles
                                order={order}
                                setLightboxSrc={setLightboxSrc}
                              />
                            </div>
                          ) : null}
                          {order.passport_manual_entry ? (
                            <div className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                              {t('orderDetail.manualEntryNoOcr')}
                            </div>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            className="w-fit"
                            onClick={() => setPdOpen(false)}
                          >
                            {t('orderDetail.hide')}
                          </Button>
                        </div>
                      )}
                    </dd>
                  </div>
                ) : null}
                {!profile?.has_pd_access &&
                isCustomerUser(profile) &&
                (order.passport_main_scan_url ||
                  order.passport_registration_scan_url ||
                  order.passport_selfie_url) ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.sellerPassportScans')}</dt>
                    <dd>
                      <PassportScansTiles
                        order={order}
                        setLightboxSrc={setLightboxSrc}
                      />
                    </dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}

          {order.customer_kind !== 'individual' && (ptsData || order.brand || order.model) ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.pts')}</h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {/* Марка/Модель: приоритет pts_data (коррекция инспектора),
                    fallback на orders (что ввёл менеджер при создании).
                    Если значения разошлись — показываем подсказкой исходное
                    значение из заказа, чтобы менеджеру было видно правку.
                    См. миграцию Stryzh `20260520180100_pts_data_brand_model`. */}
                {(ptsData?.brand ?? order.brand) ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.make')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData?.brand ?? order.brand}
                      {ptsData?.brand && order.brand && ptsData.brand !== order.brand ? (
                        <span className="ml-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
                          ({t('orderDetail.inOrder', { value: order.brand })})
                        </span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                {(ptsData?.model ?? order.model) ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.model')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData?.model ?? order.model}
                      {ptsData?.model && order.model && ptsData.model !== order.model ? (
                        <span className="ml-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
                          ({t('orderDetail.inOrder', { value: order.model })})
                        </span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                {/* Год: приоритет `orders.vehicle_year` (ввод в форме),
                    fallback на `pts_data.year` (распознанное из ПТС). */}
                {(order.vehicle_year ?? ptsData?.year) != null ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.year')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {order.vehicle_year ?? ptsData?.year}
                    </dd>
                  </div>
                ) : null}
                {order.license_plate ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.plate')}</dt>
                    <dd className="font-mono text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {formatLicensePlate(order.license_plate)}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.color ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.color')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData.color}
                    </dd>
                  </div>
                ) : null}
                {(ptsData?.vin ?? order.vin) ? (
                  <div>
                    <dt className="stryzh-input-label">VIN</dt>
                    <dd className="font-mono text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData?.vin ?? order.vin}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.body_number ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.bodyNumber')}</dt>
                    <dd className="font-mono text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData.body_number}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.chassis_number ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.chassisNumber')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData.chassis_number}
                    </dd>
                  </div>
                ) : null}
                {(ptsData?.serie || ptsData?.number) ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.ptsSeriesNumber')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {[ptsData?.serie, ptsData?.number].filter(Boolean).join(' ')}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.issuer ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.passportIssuedBy')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData.issuer}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.issue_date ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.passportIssueDate')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {ptsData.issue_date}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.is_electronic ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.ptsType')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {t('orderDetail.electronicPts')}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.title_scan_url ? (
                  <div className="sm:col-span-2">
                    <dt className="stryzh-input-label">{t('orderDetail.titleScan')}</dt>
                    <dd>
                      {(() => {
                        const url = resolveStoredPublicUrl(ptsData.title_scan_url) ?? ''
                        return (
                          <button
                            type="button"
                            className="overflow-hidden rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--stryzh-color-accent-orange)]"
                            onClick={() => setLightboxSrc(url)}
                            aria-label={t('orderDetail.openTitleScanAria')}
                          >
                            <img
                              src={url}
                              alt=""
                              className="h-32 w-auto max-w-[260px] object-cover"
                            />
                          </button>
                        )
                      })()}
                    </dd>
                  </div>
                ) : null}
                {ptsData?.manual_entry ? (
                  <div className="sm:col-span-2">
                    <dd className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                      {t('orderDetail.manualEntryNoRecognition')}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}

          {hasDkpContent ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.dkp')}</h2>
              <dl className="grid gap-4">
                {latestDkp?.contract_num ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.contractNum')}</dt>
                    <dd className="font-mono text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {latestDkp.contract_num}
                    </dd>
                  </div>
                ) : null}
                {latestDkp?.contract_date ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.contractDate')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {formatMoscowDateOnly(latestDkp.contract_date)}
                    </dd>
                  </div>
                ) : null}
                {latestDkp ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.generated')}</dt>
                    <dd className="flex flex-wrap items-baseline gap-3 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      <span>{formatMoscowDateTime(latestDkp.generated_at)}</span>
                      <button
                        type="button"
                        className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        onClick={async () => {
                          const url = await getDkpDocumentSignedUrl(latestDkp.pdf_path)
                          if (url) window.open(url, '_blank', 'noopener,noreferrer')
                        }}
                      >
                        {t('orderDetail.downloadPdf')}
                      </button>
                    </dd>
                  </div>
                ) : null}
                {order.dkp_formed_at ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.dkpFormed')}</dt>
                    <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {formatMoscowDateTime(order.dkp_formed_at)}
                    </dd>
                  </div>
                ) : null}
                {order.dkp_template_url ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.dkpTemplate')}</dt>
                    <dd>
                      <a
                        className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] font-medium text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        href={order.dkp_template_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('orderDetail.openTemplate')}
                      </a>
                    </dd>
                  </div>
                ) : null}
                {order.signed_dkp_page_urls?.length ? (
                  <div>
                    <dt className="stryzh-input-label">{t('orderDetail.signedDkp')}</dt>
                    <dd className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {order.signed_dkp_page_urls.map((rawUrl, i) => {
                          const url = resolveStoredPublicUrl(rawUrl) ?? rawUrl
                          return (
                            <button
                              key={`${rawUrl}-${i}`}
                              type="button"
                              className="overflow-hidden rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--stryzh-color-accent-orange)]"
                              onClick={() => setLightboxSrc(url)}
                              aria-label={t('orderDetail.signedDkpAria', { n: i + 1 })}
                            >
                              <img
                                src={url}
                                alt=""
                                className="h-28 w-auto max-w-[200px] object-cover"
                              />
                            </button>
                          )
                        })}
                      </div>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}

          {/* Адрес доставки (пункт 11 теста 2026-05-19) — показываем когда
              заказ уже дошёл до этапа перевода денег или транспортировки,
              ИЛИ когда поля уже заполнены (для COMPLETED — историческое
              состояние).
              canReset: только admin/dispatcher; в БД RPC всё равно проверит
              роль, эта проверка — для UI, чтобы не показывать недоступную
              кнопку. */}
          {order.status === 'PAYMENT_TRANSFER' ||
          order.status === 'TRANSPORTATION' ||
          order.delivery_address ||
          order.delivery_address_confirmed_at ? (
            <DeliveryAddressBlock
              order={order}
              canReset={
                profile?.role === 'admin' || profile?.role === 'dispatcher'
              }
            />
          ) : null}

          {showTransportBlock ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('orderDetail.transportation')}</h2>
              <dl className="grid gap-4">
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.transportStarted')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.transportation_started_at
                      ? formatMoscowDateTime(order.transportation_started_at)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.transportFinished')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.vehicle_handed_to_customer_at
                      ? formatMoscowDateTime(order.vehicle_handed_to_customer_at)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="stryzh-input-label">{t('orderDetail.transportConfirmed')}</dt>
                  <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                    {order.transportation_confirmed_at
                      ? formatMoscowDateTime(order.transportation_confirmed_at)
                      : '—'}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('orderDetail.stages')}</h2>
            <ol className="space-y-3 border-l border-[var(--stryzh-color-border-default)] pl-4">
              {[
                ...history.map((h) => ({
                  key: h.id,
                  time: h.changed_at,
                  text: `${h.history_label ?? mobileStageLabel(h.to_status)} · ${h.changed_by_profile?.full_name ?? profileName(h.changed_by)}`,
                })),
                ...synthetic.map((s, i) => ({
                  key: `syn-${i}`,
                  time: s.t,
                  text: s.label,
                })),
              ]
                .sort(
                  (a, b) =>
                    new Date(b.time).getTime() - new Date(a.time).getTime(),
                )
                .map((row) => (
                  <li key={row.key} className="relative">
                    <span className="absolute top-1.5 h-2 w-2 rounded-full bg-[var(--stryzh-color-accent-orange)] [left:calc(-1*var(--stryzh-layout-timeline-dot-offset))]" />
                    <div className="text-xs text-[var(--stryzh-color-text-secondary)]">
                      {formatMoscowDateTime(row.time)}
                    </div>
                    <div className="text-sm">{row.text}</div>
                  </li>
                ))}
            </ol>
          </section>

          {isStaff(profile) ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-1">{t('orderDetail.internalComment')}</h2>
              <p className="mb-3 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                {t('orderDetail.internalCommentHint')}
              </p>
              <OrderComments orderId={id} />
            </section>
          ) : null}

          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('orderDetail.chatReadOnly')}</h2>
            <div className="max-h-[var(--stryzh-layout-order-chat-max-height)] space-y-2 overflow-y-auto text-sm">
              {chat.length === 0 ? (
                <p className="text-[var(--stryzh-color-text-secondary)]">{t('common.noData')}</p>
              ) : (
                chat.map((m) => (
                  <div
                    key={m.id}
                    className="rounded-[var(--stryzh-radius-md)] bg-[color:var(--stryzh-color-fill-input)] p-2"
                  >
                    <div className="text-xs text-[var(--stryzh-color-text-secondary)]">
                      {m.author_name} · {formatMoscowDateTime(m.created_at)}
                    </div>
                    <div>{m.body}</div>
                    {m.attachments?.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.attachments.map((a, i) => {
                          const url = resolveStoredPublicUrl(a.url) ?? a.url
                          return (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="block"
                            >
                              {a.type === 'image' ? (
                                <img
                                  src={url}
                                  alt=""
                                  className="max-h-32 rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] object-cover"
                                />
                              ) : (
                                <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]">
                                  {t('orderDetail.attachment')}
                                </span>
                              )}
                            </a>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>

      <InspectorPicker
        // Заново при каждом открытии — «время осмотра» берётся из текущего заказа.
        key={inspectorPickerOpen ? 'open' : 'closed'}
        open={inspectorPickerOpen}
        onClose={() => setInspectorPickerOpen(false)}
        inspectionAt={order.inspection_scheduled_at ?? null}
        onPick={(inspectorId, inspectionAt) => mutAssignInspector.mutate({ inspectorId, inspectionAt })}
        isPending={mutAssignInspector.isPending}
      />

      {statusOpen ? (
        <Modal
          title={t('orderDetail.statusModalTitle')}
          onClose={() => {
            setStatusOpen(false)
            setTransitionFieldError(null)
            setReportUrlInput('')
            setFinalPriceInput('')
          }}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStatusOpen(false)
                  setTransitionFieldError(null)
                  setReportUrlInput('')
                  setFinalPriceInput('')
                }}
              >
                {t('orderDetail.close')}
              </Button>
              <Button
                variant="primary"
                disabled={!nextStatus || mutStatus.isPending}
                onClick={() => {
                  // Для перевода в «Назначен» обязателен ручной выбор
                  // инспектора — открываем пикер вместо немедленной смены
                  // статуса. Сама смена + запись inspector_id произойдёт
                  // в onPick через RPC admin_assign_inspector.
                  if (nextStatus === 'ASSIGNED') {
                    setStatusOpen(false)
                    setInspectorPickerOpen(true)
                    return
                  }
                  // Терминальные переходы требуют явного второго подтверждения
                  // (см. UX-правило). Не мутируем, пока админ не подтвердит.
                  if (nextStatus && isTerminalStatus(nextStatus)) {
                    setTerminalConfirmOpen(true)
                    return
                  }
                  mutStatus.mutate()
                }}
              >
                {t('orderDetail.confirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-[var(--stryzh-color-text-secondary)]">
              {t('orderDetail.allowedTransitions')}
            </p>
            <Select<OrderStatus>
              value={nextStatus}
              onChange={(v) => {
                setNextStatus(v as OrderStatus | '')
                setTransitionFieldError(null)
                // Префиллим инпуты текущими значениями, если они есть.
                if (v === 'APPRAISAL' && order?.status === 'INSPECTION') {
                  setReportUrlInput(order?.external_inspection_report_url ?? '')
                }
                // C2C →COMPLETED: тот же input, что и для APPRAISAL.
                if (
                  v === 'COMPLETED' &&
                  order?.customer_kind === 'individual'
                ) {
                  setReportUrlInput(order?.external_inspection_report_url ?? '')
                }
                if (v === 'BUYING' && order?.status === 'APPRAISAL') {
                  setFinalPriceInput(
                    order?.final_price ? formatGroupedInt(order.final_price) : '',
                  )
                }
              }}
              options={targets.map((s) => ({ value: s, label: statusLabelRu(s) }))}
              emptyLabel={t('orderDetail.selectPlaceholder')}
            />
            {order?.status === 'INSPECTION' && nextStatus === 'APPRAISAL' && (
              <div className="space-y-1">
                <label className="text-xs text-[var(--stryzh-color-text-secondary)]">
                  {t('orderDetail.inspectionResultUrl')}
                  {order?.external_inspection_report_url ? t('orderDetail.currentValueSuffix') : ''}
                </label>
                <input
                  type="url"
                  inputMode="url"
                  placeholder="https://demo.carinspect.ru/report/..."
                  value={reportUrlInput}
                  onChange={(e) => setReportUrlInput(e.target.value)}
                  className="h-9 w-full rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                />
                <p className="text-xs text-[var(--stryzh-color-text-secondary)]">
                  {t('orderDetail.inspectionUrlHint')}
                </p>
              </div>
            )}
            {nextStatus === 'COMPLETED' &&
              order?.customer_kind === 'individual' &&
              !order?.external_inspection_report_url && (
                <div className="space-y-1">
                  <label className="text-xs text-[var(--stryzh-color-text-secondary)]">
                    {t('orderDetail.inspectionReportLink')}
                  </label>
                  <input
                    type="url"
                    inputMode="url"
                    placeholder="https://demo.carinspect.ru/report/..."
                    value={reportUrlInput}
                    onChange={(e) => setReportUrlInput(e.target.value)}
                    className="h-9 w-full rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                  />
                  <p className="text-xs text-[var(--stryzh-color-text-secondary)]">
                    {t('orderDetail.inspectionReportHint')}
                  </p>
                </div>
              )}
            {order?.status === 'APPRAISAL' && nextStatus === 'BUYING' && (
              <div className="space-y-1">
                <label className="text-xs text-[var(--stryzh-color-text-secondary)]">
                  {t('orderDetail.buyoutPriceRub')}
                  {order?.final_price ? t('orderDetail.currentValueSuffix') : ''}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('orderDetail.buyoutPricePlaceholder')}
                  value={finalPriceInput}
                  onChange={(e) => {
                    const parsed = parseGroupedIntInput(e.target.value)
                    setFinalPriceInput(parsed == null ? '' : formatGroupedInt(parsed))
                  }}
                  className="h-9 w-full rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                />
                <p className="text-xs text-[var(--stryzh-color-text-secondary)]">
                  {t('orderDetail.buyoutPriceHint')}
                </p>
              </div>
            )}
            {transitionFieldError && (
              <p className="text-xs text-[var(--stryzh-color-error)]">
                {transitionFieldError}
              </p>
            )}
          </div>
        </Modal>
      ) : null}

      <Lightbox
        open={lightboxSrc != null && lightboxSrc.length > 0}
        src={lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />

      {cancelOpen ? (
        <Modal
          title={t('orderDetail.cancelModalTitle')}
          onClose={() => setCancelOpen(false)}
          footer={
            <>
              <Button variant="outline" onClick={() => setCancelOpen(false)}>
                {t('orderDetail.close')}
              </Button>
              <Button
                variant="danger"
                disabled={!reason.trim() || mutCancel.isPending}
                onClick={() => setCancelConfirmOpen(true)}
              >
                {t('orderDetail.cancelModalCancel')}
              </Button>
            </>
          }
        >
          {strongCancel ? (
            <p className="mb-3 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)]">
              {t('orderDetail.strongCancelWarning', { status: statusLabelRu(order.status as OrderStatus) })}
            </p>
          ) : null}
          <Input
            label={t('orderDetail.cancelReasonLabel')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Modal>
      ) : null}

      <ConfirmDialog
        open={terminalConfirmOpen}
        title={t('orderDetail.terminalConfirmTitle')}
        confirmLabel={mutStatus.isPending ? t('orderDetail.confirming') : t('orderDetail.confirm')}
        cancelLabel={t('orderDetail.back')}
        confirmVariant="danger"
        onCancel={() => {
          if (mutStatus.isPending) return
          setTerminalConfirmOpen(false)
        }}
        onConfirm={() => {
          if (mutStatus.isPending) return
          mutStatus.mutate()
        }}
      >
        <p>
          {t('orderDetail.terminalConfirmBody', {
            status: nextStatus ? statusLabelRu(nextStatus as OrderStatus) : '',
          })}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={returnSearchOpen}
        title={t('orderDetail.returnSearchTitle')}
        confirmLabel={mutReturnSearch.isPending ? t('orderDetail.returning') : t('orderDetail.returnToSearch')}
        cancelLabel={t('orderDetail.back')}
        confirmVariant="primary"
        onCancel={() => {
          if (mutReturnSearch.isPending) return
          setReturnSearchOpen(false)
        }}
        onConfirm={() => {
          if (mutReturnSearch.isPending) return
          mutReturnSearch.mutate()
        }}
      >
        <p>
          {t('orderDetail.returnSearchBodyBase')}
          {order.inspector_id ? t('orderDetail.returnSearchBodyInspector') : ''}
          {t('orderDetail.returnSearchBodyTail')}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={cancelConfirmOpen}
        title={t('orderDetail.cancelConfirmTitle')}
        confirmLabel={mutCancel.isPending ? t('orderDetail.cancelling') : t('orderDetail.cancelOrder')}
        cancelLabel={t('orderDetail.back')}
        confirmVariant="danger"
        onCancel={() => {
          if (mutCancel.isPending) return
          setCancelConfirmOpen(false)
        }}
        onConfirm={() => {
          if (mutCancel.isPending) return
          mutCancel.mutate()
        }}
      >
        <p>
          {t('orderDetail.cancelConfirmBodyBase')}
          {strongCancel ? t('orderDetail.cancelConfirmBodyStrong') : ''}
        </p>
      </ConfirmDialog>
    </div>
  )
}

/**
 * Лента внутренних комментариев заказа (append-only, новые сверху) + ввод.
 * Комментарии нельзя редактировать/удалять (RLS). Автор проставляется БД из
 * auth.uid(). Своя query + realtime через invalidate (таблица order_comments
 * в publication). Рендерится внутри staff-only секции «Внутренний комментарий».
 */
function OrderComments({ orderId }: { orderId: string }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [body, setBody] = useState('')

  const commentsQ = useQuery({
    queryKey: ['order-comments', orderId],
    queryFn: () => listOrderComments(orderId),
    enabled: !!orderId,
  })

  const addMut = useMutation({
    mutationFn: () => addOrderComment(orderId, body),
    onSuccess: () => {
      setBody('')
      void qc.invalidateQueries({ queryKey: ['order-comments', orderId] })
    },
  })

  const comments = commentsQ.data ?? []
  const canSubmit = body.trim() !== '' && !addMut.isPending

  return (
    <div className="flex flex-col gap-2">
      <textarea
        className="w-full resize-y rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] focus:border-[color:var(--stryzh-color-accent-orange)] focus:outline-none"
        rows={3}
        placeholder={t('orderDetail.commentPlaceholder')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
            e.preventDefault()
            addMut.mutate()
          }
        }}
      />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => addMut.mutate()} disabled={!canSubmit}>
          {addMut.isPending ? t('orderDetail.adding') : t('orderDetail.add')}
        </Button>
      </div>
      {addMut.isError ? (
        <div className="text-xs text-[color:var(--stryzh-color-accent-orange)]">
          {t('orderDetail.addCommentError', { message: (addMut.error as Error).message })}
        </div>
      ) : null}

      <div className="mt-2">
        {commentsQ.isLoading ? (
          <div className="stryzh-text-muted text-sm">{t('orderDetail.loadingComments')}</div>
        ) : comments.length === 0 ? (
          <p className="stryzh-text-muted text-sm">{t('orderDetail.noComments')}</p>
        ) : (
          <ol className="space-y-2 text-sm">
            {comments.map((c) => (
              <OrderCommentItem key={c.id} comment={c} />
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

function OrderCommentItem({ comment }: { comment: OrderComment }) {
  const { t } = useTranslation()
  return (
    <li className="rounded border border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-void)] px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-[color:var(--stryzh-color-text-primary)]">
          {comment.author_name || t('orderDetail.commentMigrated')}
        </span>
        <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
          {formatMoscowDateTime(comment.created_at)}
        </span>
      </div>
      <div className="mt-1 whitespace-pre-wrap break-words text-[color:var(--stryzh-color-text-primary)]">
        {comment.body}
      </div>
    </li>
  )
}
